/**
 * @file ContactsTab.js
 * @description Contacts tab component for the Client SSO Wizard.
 */

import React, { useState, useEffect } from "react";
import { fetchClientContacts } from "../../../services/ssoService";
import Spinner from "../../ui/spinners/Spinner";
import Toast from "../../ui/toasts/Toast";
import styles from "../sso.module.scss";
import NotFoundBox from "../../ui/no_data_indicators/NotFoundBox";
import { getText } from '../../../i18n';

const SortDirections = {
  ASCENDING: "asc",
  DESCENDING: "desc",
};

const ContactsTab = ({ data }) => {
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "firstName",
    direction: SortDirections.ASCENDING,
  });

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setIsLoading(true);
      const fetchedContacts = await fetchClientContacts();
      setContacts(fetchedContacts);
    } catch (error) {
      console.error("Failed to fetch client contacts", error);
      Toast.error(getText('msg_f5c1e7f777e5'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key &&
        prevConfig.direction === SortDirections.ASCENDING
          ? SortDirections.DESCENDING
          : SortDirections.ASCENDING,
    }));
  };

  const sortedContacts = React.useMemo(() => {
    const sortableContacts = [...contacts];
    if (sortConfig.key) {
      sortableContacts.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === SortDirections.ASCENDING ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === SortDirections.ASCENDING ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableContacts;
  }, [contacts, sortConfig]);

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    return (
      <span className={`px-2 ${styles.sortArrowIcon}`}>
        <i
          className={`bi bi-chevron-${
            sortConfig.direction === SortDirections.ASCENDING ? "up" : "down"
          }`}
        ></i>
      </span>
    );
  };
  

  if (isLoading) {
    return <Spinner width={50} className={styles.loadingSpinner} />;
  }

  return (
    <div className="col-12 col-sm-12 col-md-12 col-lg-10 col-xl-8 m-auto">
      {contacts.length === 0 ? (
        <NotFoundBox
          backgroundColor="#f0f4ff"
          title={getText('msg_49d336b1d273')}
          description={getText('msg_0443c2bab33a')}
        />
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle ing-table-striped">
            <thead className="custom-table-header table-light">
              <tr>
                <th
                  onClick={() => handleSort("firstName")}
                  style={{ cursor: "pointer" }}
                >
                  {getText('msg_709a23220f2c')} {renderSortArrow("firstName")}
                </th>
                <th
                  onClick={() => handleSort("emailAddress")}
                  style={{ cursor: "pointer" }}
                >
                  {getText('msg_84add5b29527')} {renderSortArrow("emailAddress")}
                </th>
                <th
                  onClick={() => handleSort("jobTitle")}
                  style={{ cursor: "pointer" }}
                >
                  {getText('msg_d42977925117')} {renderSortArrow("jobTitle")}
                </th>
                <th
                  onClick={() => handleSort("contactType")}
                  style={{ cursor: "pointer" }}
                >
                  {getText('msg_fcbedcab55ea')} {renderSortArrow("contactType")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedContacts.map((contact) => (
                <tr key={contact.id}>
                  <td>{`${contact.firstName} ${contact.lastName}`}</td>
                  <td>{contact.emailAddress}</td>
                  <td>{contact.jobTitle}</td>
                  <td>{contact.contactType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ContactsTab;
